import React, { useEffect, useRef } from 'react';

const VettingProcess: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      number: "01",
      title: "Technical Depth Test",
      description: "Code review and architecture challenges specifically mapped to standard enterprise protocols."
    },
    {
      number: "02",
      title: "Operational Alignment",
      description: "Evaluating behavioral competencies and fit for complex Agile/Scrum environments."
    },
    {
      number: "03",
      title: "Track Record Audit",
      description: "Direct verification of past delivery outcomes and production-level performance metrics."
    },
    {
      number: "04",
      title: "SLA Adherence",
      description: "Ongoing monitoring to ensure every resource meets strict internal delivery standards."
    }
  ];

  const stats = [
    { label: "Acceptance Rate", value: "2.4%" },
    { label: "Time-to-Fill", value: "48 Hrs" },
    { label: "Retention Rate", value: "96%" },
    { label: "Technical Tests", value: "150+" }
  ];

  return (
    <section ref={sectionRef} className="py-16 md:py-32 bg-white border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-16 md:gap-24 items-start">
          <div className="lg:col-span-1 reveal">
            <div className="mono text-xs text-blue-600 mb-6 tracking-widest uppercase">// Verification Protocol</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 tracking-tight">The Grid-Vetting Process</h2>
            <p className="text-base md:text-lg text-slate-500 font-light mb-12 leading-relaxed">
              We eliminate technical risk through a multi-stage verification framework designed by senior architects.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="p-5 bg-slate-50 rounded-sm border border-slate-100 group hover:bg-white hover:border-blue-100 hover:shadow-lg transition-all">
                  <div className="text-2xl font-bold text-slate-900 tracking-tight group-hover:text-blue-600 transition-colors">{stat.value}</div>
                  <div className="mono text-[8px] text-slate-500 uppercase tracking-widest mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 grid md:grid-cols-2 gap-px bg-slate-100 border border-slate-100 rounded-sm overflow-hidden">
            {steps.map((step, i) => (
              <div 
                key={i} 
                className="reveal bg-white p-10 md:p-12 relative group hover:bg-slate-50 transition-colors" 
                style={{ transitionDelay: `${(i + 1) * 150}ms` }}
              >
                <div className="text-7xl font-black text-slate-50 absolute top-6 right-6 z-0 group-hover:text-blue-50/50 transition-colors pointer-events-none select-none">
                  {step.number}
                </div>
                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-slate-900 mb-4 tracking-tight group-hover:text-blue-600 transition-colors">{step.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed font-light">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VettingProcess;